import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { StaticPage, staticHead } from "@/components/StaticPage";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
  head: () =>
    staticHead({
      title: "Contact AutoSpecData — Corrections & Questions",
      description:
        "Report a wrong vehicle spec, request a make or model, or ask a question. Send a message to the AutoSpecData team and we'll reply by email.",
      path: "/contact",
    }),
});

const SUBJECTS = [
  "Data correction",
  "Missing make or model",
  "General question",
  "Advertising or partnership",
  "Privacy request",
];

function ContactPage() {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    // Honeypot: real users never fill this.
    if ((fd.get("company") as string)?.length) {
      setStatus("sent");
      return;
    }
    const payload = {
      name: String(fd.get("name") ?? "").trim(),
      email: String(fd.get("email") ?? "").trim(),
      subject: String(fd.get("subject") ?? "").trim(),
      message: String(fd.get("message") ?? "").trim(),
      page_url: typeof window !== "undefined" ? window.location.href : null,
    };
    if (payload.message.length < 5) {
      setStatus("error");
      setError("Please write a little more detail.");
      return;
    }
    setStatus("sending");
    const { error: insertError } = await supabase.from("contact_messages").insert(payload);
    if (insertError) {
      setStatus("error");
      setError("That didn't send. Please try again in a moment.");
      return;
    }
    form.reset();
    setStatus("sent");
  }

  return (
    <StaticPage
      title="Contact"
      intro="Found a spec that doesn't match your vehicle's documentation, or want a make added? Send us the details and we'll look into it."
    >
      <section>
        <h2>Email us directly</h2>
        <p>
          For corrections, data requests, or partnerships, reach us at{" "}
          <a
            href="mailto:contact@autospecdata.com"
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
          >
            contact@autospecdata.com
          </a>
          . For press, advertising, and bulk data enquiries, use{" "}
          <a
            href="mailto:info@autospecdata.com"
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
          >
            info@autospecdata.com
          </a>
          .
        </p>
      </section>

      <section>
        <h2>Before you write</h2>
        <ul>
          <li>
            For a data correction, include the <strong>year, make, model, and trim</strong>,
            the value you believe is wrong, and the document you're comparing against.
          </li>
          <li>
            Where our numbers come from is documented on the{" "}
            <Link to="/methodology">methodology page</Link>.
          </li>
          <li>
            We can't diagnose a specific vehicle problem — your dealer or a qualified
            technician is the right call for that.
          </li>
        </ul>
      </section>

      <section>
        <h2>Send a message</h2>
        {status === "sent" ? (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-5 text-foreground">
            <p className="font-semibold">Message received.</p>
            <p className="mt-1 text-muted-foreground">
              Thanks — we read every correction. If a reply is needed, it'll come to the
              email address you gave.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4 not-prose">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-foreground font-medium">Your name</span>
                <input
                  name="name"
                  required
                  maxLength={120}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground outline-none focus:border-primary"
                />
              </label>
              <label className="block">
                <span className="text-foreground font-medium">Email</span>
                <input
                  name="email"
                  type="email"
                  required
                  maxLength={200}
                  className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground outline-none focus:border-primary"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-foreground font-medium">Subject</span>
              <select
                name="subject"
                required
                defaultValue={SUBJECTS[0]}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground outline-none focus:border-primary"
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-foreground font-medium">Message</span>
              <textarea
                name="message"
                required
                rows={6}
                maxLength={5000}
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground outline-none focus:border-primary"
              />
            </label>
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
            />
            {status === "error" ? (
              <p className="text-destructive">{error}</p>
            ) : null}
            <button
              type="submit"
              disabled={status === "sending"}
              className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60"
            >
              {status === "sending" ? "Sending…" : "Send message"}
            </button>
            <p className="text-xs text-muted-foreground">
              We use your email only to reply. See the{" "}
              <Link to="/privacy">privacy policy</Link>.
            </p>
          </form>
        )}
      </section>

      <section>
        <h2>What happens after you send a message</h2>
        <p>
          Every message lands in a single reviewed queue rather than an automated ticket
          system. Data corrections are checked first: we pull the original EPA, Department of
          Energy, and NHTSA vPIC records for the year, make, model, and trim you named, compare
          them against the value published on the page, and either correct the record or reply
          explaining why the source data reads the way it does. Most corrections are reviewed
          within two to three business days, and a fix goes live with the next data refresh
          rather than waiting for a scheduled release.
        </p>
        <p>
          Requests for a missing make, model, or model year are logged and grouped. A single
          request rarely triggers ingestion on its own, but repeated requests for the same
          nameplate move it up the queue — heavy-duty pickups, low-volume imports, and pre-2015
          model years are the three areas we hear about most often, and each one has been
          expanded because readers asked.
        </p>
      </section>

      <section>
        <h2>What we can and cannot help with</h2>
        <p>
          We can help with anything about the published data itself: a figure that disagrees
          with your window sticker or owner's manual, a trim that is missing from a model page,
          a comparison that pairs the wrong two vehicles, a maintenance interval that looks off
          for your engine, or a broken page. We can also explain where any individual number
          came from and which dataset and release it was drawn from.
        </p>
        <p>
          We cannot diagnose a fault on your vehicle, quote a repair, value a trade-in, provide
          VIN history or accident records, or advise on a warranty or recall claim. Recall
          lookups belong with NHTSA or your manufacturer directly, and anything mechanical
          belongs with a qualified technician who can see the car. Sending those questions here
          only delays getting you a real answer.
        </p>
      </section>

      <section>
        <h2>Press, research, and licensing</h2>
        <p>
          Journalists, researchers, and analysts are welcome to cite AutoSpecData figures with
          attribution and a link to the page the number came from. If you need a dataset cut
          that is not published — a specific class, year band, or powertrain segment — say so in
          your message and describe the outlet and deadline. We can usually turn around a
          summary table, and our{" "}
          <Link to="/methodology">methodology page</Link> documents the sourcing you will need
          for a fact-check.
        </p>
        <p>
          For partnership, advertising, or bulk data enquiries, choose the matching subject in
          the form above so the message routes correctly. Please include the scope you have in
          mind rather than a general introduction; it saves a round trip.
        </p>
      </section>

      <section>
        <h2>Privacy of what you send</h2>
        <p>
          Your name and email are used only to reply to the message you sent. We do not add
          contact-form addresses to a mailing list, share them with third parties, or use them
          for advertising. If you are making a privacy request — access, correction, or deletion
          of data we hold about you — select the privacy subject and we will handle it under the
          terms set out in the <Link to="/privacy">privacy policy</Link>.
        </p>
      </section>
    </StaticPage>
  );
}
